import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
};

const base =
  'block w-full rounded-card border border-fc-line bg-fc-paper px-3 py-2 text-base ' +
  'text-fc-ink placeholder:text-fc-muted ' +
  'focus:border-fc-gold focus:outline-none focus:ring-2 focus:ring-fc-gold/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const errored = 'border-fc-accent focus:border-fc-accent focus:ring-fc-accent/40';

/**
 * Input — base UI primitive.
 *
 * ROUND17 #11: cuando `type="password"`, agrega un botón "ver/ocultar"
 * adentro del input (toggle de visibilidad) — pattern cross-platform
 * alineado con web/admin/app. El botón cambia el `type` entre `password`
 * y `text` localmente; el estado se reinicia al desmontar.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, className = '', type, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const effectiveType = isPassword ? (revealed ? 'text' : 'password') : type;
  const inputClasses = [base, error ? errored : '', isPassword ? 'pr-11' : '', className]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-fc-ink">
        {label}
      </label>
      <div className={isPassword ? 'relative' : undefined}>
        <input
          ref={ref}
          id={inputId}
          type={effectiveType}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={inputClasses}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            aria-pressed={revealed}
            tabIndex={-1}
            className="absolute inset-y-0 right-1 my-1 inline-flex items-center justify-center rounded-card px-2 text-xs font-medium text-fc-muted transition-colors hover:bg-fc-line/40 hover:text-fc-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fc-gold"
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {hint && !error && (
        <p id={hintId} className="text-xs text-fc-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-fc-accent">
          {error}
        </p>
      )}
    </div>
  );
});

function EyeIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}
